import { span, type Mountable } from '@llui/dom';

/**
 * `<ProvenanceBadge>` — who authored this fact (per 30-design-system.md §3.4).
 *
 * Four authoring sources map to four hue families:
 *   - `gm`     — slate (the accent itself; no extra tint)
 *   - `llm`    — soft purple (matches the Pending semantic — both
 *                share the "proposed not ratified" mental space)
 *   - `player` — soft green (the specific player's color in the wider
 *                product; we leave the per-player tint to the caller
 *                and render the default green here)
 *   - `system` — neutral muted (no hue)
 *
 * The badge is small (10-11px text), pill-shaped, and pairs an
 * optional icon (Lucide `users-2`, `sparkles`, `user`, `cog`) with the
 * author label. For a screen-reader-only marker (no painted chrome)
 * pass `visualOnly: true` and a `label` that announces the provenance.
 *
 * Don't put the predicate or value in the label — those belong on the
 * fact row. Keep this scoped to "who wrote this".
 */

export type ProvenanceKind = 'gm' | 'llm' | 'player' | 'system';

export interface ProvenanceBadgeOptions {
  kind: ProvenanceKind;
  /** Either a static string or a llui reactive text node (`text(...)`). */
  label: string | Mountable;
  /** Optional leading icon (e.g. a Lucide `sparkles` for LLM). */
  icon?: Mountable;
  /** Forwarded `data-testid`. */
  testid?: string;
}

export function provenanceBadge(opts: ProvenanceBadgeOptions): Mountable {
  const children: (string | Mountable)[] = [];
  if (opts.icon) children.push(opts.icon);
  children.push(opts.label);
  return span(
    {
      class: 'dl-provenance-badge',
      'data-kind': opts.kind,
      'data-testid': opts.testid,
    },
    children,
  );
}
